import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { funnelCopy } from "../../i18n/funnel";

/**
 * Hero visual — explainer growth funnel.
 *
 * Four stacked 3D-style bands (TOFU → MOFU → BOFU → $). App icons drop into
 * the opening, coins fall out of the spout. Beside each band a callout says
 * what AppFillip does at that stage and the result (numbers count up). One
 * stage is "active" at a time: it glows, its callout lights up and the
 * sentence under the funnel explains it. Stages auto-advance every few
 * seconds; hovering a band or callout pins it. Pure SVG + framer-motion.
 *
 * Wording comes from src/i18n/funnel.js — pass `locale` (e.g. "de_DE") on
 * the market homepages; English otherwise.
 */

const CX = 190; // funnel centre line (callouts live to the right of it)

// Geometry + colours per band. Frustum: top ellipse (rxT, ryT) at yT, bottom
// ellipse at yB. Each lower band is narrower than the one above's bottom so a
// rim of its top face stays visible — that's what gives the stacked look.
const BANDS = [
  { code: "TOFU", color: "#2196F3", yT: 150, rxT: 132, ryT: 27, yB: 240, rxB: 109, ryB: 22, metric: { value: 2.4, decimals: 1, suffix: "M" } },
  { code: "MOFU", color: "#EF5F5F", yT: 258, rxT: 105, ryT: 21, yB: 338, rxB: 84, ryB: 17, metric: { value: 184, decimals: 0, suffix: "K" } },
  { code: "BOFU", color: "#F2A93B", yT: 354, rxT: 81, ryT: 16, yB: 426, rxB: 62, ryB: 13, metric: { value: 41, decimals: 0, suffix: "%" } },
  { code: "$", color: "#2D2D2D", yT: 440, rxT: 59, ryT: 12, yB: 502, rxB: 41, ryB: 9, metric: { value: 3.1, decimals: 1, suffix: "×" } },
];

const STEP_MS = 3200;

const mix = (hex, amount, to = 0) => {
  // Blend a hex colour toward black (to=0) or white (to=255) by `amount`.
  const n = parseInt(hex.slice(1), 16);
  const ch = (s) => Math.round(((n >> s) & 255) * (1 - amount) + to * amount);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
};

// Body: left edge down, front arc of bottom ellipse, right edge up, front arc
// of top ellipse back to start.
const bodyPath = ({ yT, rxT, ryT, yB, rxB, ryB }) =>
  `M${CX - rxT},${yT} L${CX - rxB},${yB} A${rxB},${ryB} 0 0 0 ${CX + rxB},${yB} L${CX + rxT},${yT} A${rxT},${ryT} 0 0 1 ${CX - rxT},${yT} Z`;

const APPS = [
  { x: -55, color: "#f97316", glyph: "A" },
  { x: 32, color: "#22c55e", glyph: "B" },
  { x: -15, color: "#8b5cf6", glyph: "C" },
  { x: 58, color: "#ec4899", glyph: "D" },
  { x: 8, color: "#06b6d4", glyph: "E" },
];
const TILE = 30;

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Counts from 0 to `value` once, on mount. */
function CountUp({ value, decimals = 0, suffix = "", duration = 1800, delay = 600 }) {
  const reduced = useReducedMotion();
  const [n, setN] = useState(reduced ? value : 0);
  useEffect(() => {
    if (reduced) return undefined;
    let frame;
    let start;
    const tick = (t) => {
      if (start === undefined) start = t;
      const p = Math.min(1, (t - start - delay) / duration);
      if (p >= 0) setN(value * (1 - Math.pow(1 - p, 3))); // ease-out cubic
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, delay, reduced]);
  return `${n.toFixed(decimals)}${suffix}`;
}

function Coin({ x, y, rx = 13, ry = 4.5 }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <ellipse cy="3" rx={rx} ry={ry} fill="#c98a16" />
      <ellipse rx={rx} ry={ry} fill="#f5b63b" />
      <ellipse rx={rx * 0.62} ry={ry * 0.62} fill="none" stroke="#e39c22" strokeWidth="1" />
      <text y="2" textAnchor="middle" fontSize="6" fontWeight="800" fill="#b67610" fontFamily="ui-sans-serif, system-ui">$</text>
    </g>
  );
}

function CoinStack({ x, y, n }) {
  return (
    <g>
      {Array.from({ length: n }).map((_, i) => (
        <Coin key={i} x={x} y={y - i * 4.2} />
      ))}
    </g>
  );
}

export default function HeroFunnel({ locale }) {
  const reduced = useReducedMotion();
  const wide = useMediaQuery("(min-width: 768px)");
  const copy = funnelCopy(locale);
  const [active, setActive] = useState(0);
  const [pinned, setPinned] = useState(false);

  // Auto-advance through the stages unless the visitor is hovering one.
  useEffect(() => {
    if (pinned) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % BANDS.length), STEP_MS);
    return () => clearInterval(id);
  }, [pinned]);

  const top = BANDS[0];
  const spout = BANDS[BANDS.length - 1];
  const last = BANDS.length - 1;
  const pin = (i) => () => {
    setActive(i);
    setPinned(true);
  };
  const unpin = () => setPinned(false);
  const text = (i) => copy.bands[i];

  return (
    <div className="relative" data-testid="hero-funnel">
      <p className="text-overline text-electric mb-1">{copy.title}</p>

      <svg
        // Phones: crop to the funnel itself (callouts move into the caption below).
        viewBox={wide ? "0 0 540 600" : "50 0 280 600"}
        className="w-full h-auto"
        role="img"
        aria-label={copy.bands.map((b) => b.label).join(" → ")}
      >
        <defs>
          <linearGradient id="hf-cone" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2196F3" stopOpacity="0" />
            <stop offset="100%" stopColor="#2196F3" stopOpacity="0.14" />
          </linearGradient>
          <linearGradient id="hf-gloss" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          {BANDS.map((b, i) => (
            <linearGradient key={i} id={`hf-body-${i}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={mix(b.color, 0.35)} />
              <stop offset="45%" stopColor={b.color} />
              <stop offset="100%" stopColor={mix(b.color, 0.3)} />
            </linearGradient>
          ))}
          <clipPath id="hf-drop-clip">
            <rect x="0" y="0" width="540" height={top.yT + top.ryT - 6} />
          </clipPath>
        </defs>

        {/* Light cone above the opening */}
        <motion.path
          d={`M${CX - 70},0 L${CX + 70},0 L${CX + top.rxT},${top.yT} L${CX - top.rxT},${top.yT} Z`}
          fill="url(#hf-cone)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        />

        {/* Ground shadow + coin pile */}
        <ellipse cx={CX} cy="578" rx="120" ry="12" fill="#2D2D2D" opacity="0.07" />
        <motion.g initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.6 }}>
          <CoinStack x={CX - 42} y={566} n={5} />
          <CoinStack x={CX + 4} y={570} n={7} />
          <CoinStack x={CX + 48} y={565} n={4} />
          <Coin x={CX - 16} y={548} />
          <Coin x={CX + 26} y={541} />
        </motion.g>

        {/* Falling coins */}
        {!reduced &&
          [0, 0.75, 1.5].map((delay, i) => (
            <motion.g
              key={i}
              initial={{ opacity: 0 }}
              animate={{
                y: [spout.yB + 4, spout.yB + 30, 548],
                x: [0, (i - 1) * 8, (i - 1) * 16],
                rotate: [0, 40, 95],
                opacity: [0, 1, 0],
              }}
              style={{ transformOrigin: `${CX}px ${spout.yB}px` }}
              transition={{ duration: 1.3, delay, repeat: Infinity, repeatDelay: 0.95, ease: "easeIn", times: [0, 0.25, 1] }}
            >
              <Coin x={CX} y={0} />
            </motion.g>
          ))}

        {/* Bands — drawn bottom-up so each upper band sits in front */}
        {[...BANDS].reverse().map((b, ri) => {
          const i = last - ri;
          const isActive = i === active;
          const yMid = (b.yT + b.yB) / 2;
          const labelY = yMid + b.ryT * 0.4;
          return (
            <motion.g
              key={i}
              onMouseEnter={pin(i)}
              onMouseLeave={unpin}
              style={{ transformOrigin: `${CX}px ${yMid}px`, cursor: "default" }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0, scale: isActive ? 1.04 : 1 }}
              transition={{ opacity: { delay: 0.2 + i * 0.12, duration: 0.5 }, y: { delay: 0.2 + i * 0.12, duration: 0.5 }, scale: { duration: 0.45, ease: "easeOut" } }}
            >
              {/* top face (rim) */}
              <ellipse cx={CX} cy={b.yT} rx={b.rxT} ry={b.ryT} fill={mix(b.color, 0.42, 255)} />
              <ellipse cx={CX} cy={b.yT} rx={b.rxT * 0.86} ry={b.ryT * 0.8} fill={mix(b.color, 0.25)} opacity="0.9" />
              {/* body */}
              <path d={bodyPath(b)} fill={`url(#hf-body-${i})`} />
              <path d={bodyPath(b)} fill="url(#hf-gloss)" />
              {/* active glow */}
              <motion.path d={bodyPath(b)} fill="#fff" animate={{ opacity: isActive ? 0.16 : 0 }} transition={{ duration: 0.4 }} />
              {/* label */}
              {i === last ? (
                <text x={CX} y={labelY + 9} textAnchor="middle" fontSize="28" fontWeight="800" fill="#fff" fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
                  $
                </text>
              ) : (
                <>
                  <text x={CX} y={labelY - 7} textAnchor="middle" fontSize="7.5" fontWeight="800" letterSpacing="2.5" fill="#fff" opacity="0.8" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
                    {b.code}
                  </text>
                  <text x={CX} y={labelY + 9} textAnchor="middle" fontSize="16" fontWeight="800" fill="#fff" fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
                    {text(i).label}
                  </text>
                </>
              )}
            </motion.g>
          );
        })}

        {/* Apps dropping into the opening */}
        <g clipPath="url(#hf-drop-clip)">
          {APPS.map((app, i) => (
            <motion.g
              key={app.glyph}
              initial={reduced ? { x: CX + app.x - TILE / 2, y: 70 + i * 14, opacity: 1 } : { opacity: 0 }}
              animate={
                reduced
                  ? undefined
                  : {
                      x: [CX + app.x - TILE / 2, CX + app.x * 0.45 - TILE / 2, CX + app.x * 0.2 - TILE / 2],
                      y: [-40, top.yT - 40, top.yT + 4],
                      scale: [1, 0.9, 0.55],
                      opacity: [0, 1, 1],
                    }
              }
              style={{ transformOrigin: `${TILE / 2}px ${TILE / 2}px` }}
              transition={{ duration: 2.4, delay: i * 0.95, repeat: Infinity, repeatDelay: 2.35, ease: "easeIn", times: [0, 0.65, 1] }}
            >
              <rect width={TILE} height={TILE} rx="8" fill={app.color} />
              <rect width={TILE} height={TILE} rx="8" fill="url(#hf-gloss)" />
              <text x={TILE / 2} y={TILE / 2 + 5} textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
                {app.glyph}
              </text>
            </motion.g>
          ))}
        </g>

        {/* Callouts — what we do at each stage + the result (md and up) */}
        {wide &&
          BANDS.map((b, i) => {
            const isActive = i === active;
            const yMid = (b.yT + b.yB) / 2;
            const edgeX = CX + (b.rxT + b.rxB) / 2 + 4;
            const x = 352;
            const w = 180;
            const h = 56;
            return (
              <motion.g
                key={i}
                onMouseEnter={pin(i)}
                onMouseLeave={unpin}
                style={{ cursor: "default" }}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + i * 0.12, duration: 0.5 }}
              >
                <motion.line
                  x1={edgeX} y1={yMid} x2={x} y2={yMid}
                  stroke={b.color}
                  strokeWidth="1.25"
                  strokeDasharray={isActive ? "0" : "3 3"}
                  animate={{ opacity: isActive ? 1 : 0.45 }}
                />
                <circle cx={edgeX} cy={yMid} r="3" fill={b.color} />
                <motion.rect
                  x={x + 3} y={yMid - h / 2 + 3} width={w} height={h}
                  fill={b.color}
                  animate={{ opacity: isActive ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.rect
                  x={x} y={yMid - h / 2} width={w} height={h}
                  fill="#fff"
                  stroke={isActive ? b.color : "#e4e4e7"}
                  strokeWidth={isActive ? 1.5 : 1}
                  animate={{ opacity: isActive ? 1 : 0.85 }}
                />
                <text x={x + 12} y={yMid - 10} fontSize="11" fontWeight="800" fill={b.color} fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
                  {text(i).label}
                </text>
                <text x={x + 12} y={yMid + 3} fontSize="8.5" fill="#71717a" fontFamily="ui-sans-serif, system-ui">
                  {text(i).does}
                </text>
                <text x={x + 12} y={yMid + 19} fontSize="14" fontWeight="800" fill="#2D2D2D" fontFamily="Cabinet Grotesk, Space Grotesk, ui-sans-serif, system-ui">
                  <CountUp {...b.metric} delay={1200 + i * 200} />
                  <tspan fontSize="8" fontWeight="600" fill="#71717a" dx="4">
                    {text(i).metricLabel.toUpperCase()}
                  </tspan>
                </text>
              </motion.g>
            );
          })}
      </svg>

      {/* Step explainer — follows the active stage */}
      <div className="mt-2 border border-zinc-200 bg-white/85 backdrop-blur px-4 py-3" aria-live="polite">
        <div className="flex items-center gap-1.5 mb-2">
          {BANDS.map((b, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`${copy.step} ${i + 1}: ${text(i).label}`}
              aria-current={i === active ? "step" : undefined}
              className="h-1.5 flex-1 max-w-10 transition-colors"
              style={{ backgroundColor: i === active ? b.color : "#e4e4e7" }}
            />
          ))}
          <span className="ml-auto text-[10px] font-bold uppercase tracking-widest" style={{ color: BANDS[active].color }}>
            {copy.step} {active + 1} / {BANDS.length} · {text(active).label}
          </span>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={active}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="text-[13px] text-zinc-600 leading-snug min-h-[2.5rem]"
          >
            {text(active).explain}
            {!wide && (
              <span className="block mt-1 text-[11px] text-zinc-500">
                {text(active).does} ·{" "}
                <strong className="text-charcoal">
                  {BANDS[active].metric.value}{BANDS[active].metric.suffix} {text(active).metricLabel}
                </strong>
              </span>
            )}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
